<template>
  <div class="widget-property-filter">
    <div class="d-flex align-center mb-2">
      <v-text-field
        v-model="search"
        placeholder="Search values..."
        dense
        outlined
        hide-details
        clearable
        prepend-inner-icon="mdi-magnify"
        class="flex-grow-1"
        @input="onSearchChange">
      </v-text-field>
    </div>

    <div v-if="loading" class="d-flex justify-center pa-4">
      <envizion-loader :size="48" :color="$vuetify.theme.dark ? '#2cc295' : '#03624c'" />
    </div>

    <template v-else>
      <v-list-item dense ripple @mousedown.prevent @click="toggleAll" class="px-0">
        <v-list-item-action class="mr-2 my-0">
          <v-icon small :color="allSelected ? 'primary' : ''">
            {{ allSelected ? 'mdi-checkbox-marked' : (someSelected ? 'mdi-minus-box' : 'mdi-checkbox-blank-outline') }}
          </v-icon>
        </v-list-item-action>
        <v-list-item-content class="py-0">
          <v-list-item-title class="text-caption font-weight-medium">
            {{ allSelected ? 'Deselect All' : 'Select All' }}
          </v-list-item-title>
        </v-list-item-content>
        <v-chip v-if="selectedValues.length" x-small color="primary" class="white--text">
          {{ selectedValues.length }}
        </v-chip>
      </v-list-item>

      <v-divider class="mb-1"></v-divider>

      <div class="widget-property-filter-list overflow-y-auto" style="max-height: 200px;">
        <v-list-item
          v-for="val in displayValues"
          :key="String(val)"
          dense
          ripple
          class="px-0"
          @click="toggleValue(val)">
          <v-list-item-action class="mr-2 my-0">
            <v-icon small :color="isSelected(val) ? 'primary' : ''">
              {{ isSelected(val) ? 'mdi-checkbox-marked' : 'mdi-checkbox-blank-outline' }}
            </v-icon>
          </v-list-item-action>
          <v-list-item-content class="py-0">
            <v-list-item-title class="text-caption">
              {{ formatValue(val) }}
            </v-list-item-title>
          </v-list-item-content>
        </v-list-item>

        <v-list-item v-if="!displayValues.length && !loading">
          <v-list-item-content>
            <v-list-item-title class="text-caption text--disabled">No values found</v-list-item-title>
          </v-list-item-content>
        </v-list-item>
      </div>
    </template>
  </div>
</template>

<script>
import apiService from '../service/dashboard-widgets.service';
import { debounce } from 'lodash';

export default {
  name: 'WidgetPropertyFilter',

  props: {
    tableName: { type: String, required: true },
    columnName: { type: String, required: true },
    dataType: { type: String, default: 'nvarchar' },
    value: { type: Array, default: () => [] },
    filterSessionId: { type: String, default: null },
  },

  data() {
    return {
      loading: false,
      search: '',
      allValues: [],
      selectedValues: this.value ? [...this.value] : [],
    };
  },

  computed: {
    displayValues() {
      if (!this.search) return this.allValues;
      const s = this.search.toLowerCase();
      return this.allValues.filter(v => String(v).toLowerCase().includes(s));
    },
    allSelected() {
      return this.displayValues.length > 0 && this.displayValues.every(v => this.isSelected(v));
    },
    someSelected() {
      return this.selectedValues.length > 0 && !this.allSelected;
    },
  },

  watch: {
    value(val) {
      this.selectedValues = val ? [...val] : [];
    },
  },

  mounted() {
    this.fetchValues();
  },

  created() {
    this.debouncedFetch = debounce(function () {
      this.fetchValues();
    }.bind(this), 400);
  },

  methods: {
    fetchValues() {
      this.loading = true;
      apiService
        .columnDistinctValues(this.tableName, this.columnName, {
          filterSessionId: this.filterSessionId,
          search: this.search || undefined,
          take: 500,
        })
        .then(res => {
          const result = res.data;
          this.allValues = Array.isArray(result) ? result : (result.data || []);
        })
        .catch(() => {
          this.allValues = [];
        })
        .finally(() => {
          this.loading = false;
        });
    },

    onSearchChange() {
      this.debouncedFetch();
    },

    isSelected(val) {
      return this.selectedValues.includes(val);
    },

    toggleValue(val) {
      const idx = this.selectedValues.indexOf(val);
      if (idx >= 0) {
        this.selectedValues.splice(idx, 1);
      } else {
        this.selectedValues.push(val);
      }
      this.$emit('input', [...this.selectedValues]);
    },

    toggleAll() {
      if (this.allSelected) {
        // Deselect all visible
        this.displayValues.forEach(v => {
          const idx = this.selectedValues.indexOf(v);
          if (idx >= 0) this.selectedValues.splice(idx, 1);
        });
      } else {
        // Select all visible
        this.displayValues.forEach(v => {
          if (!this.selectedValues.includes(v)) this.selectedValues.push(v);
        });
      }
      this.$emit('input', [...this.selectedValues]);
    },

    formatValue(val) {
      if (val === null || val === undefined) return '(empty)';
      if (this.dataType === 'datetime' || this.dataType === 'date') {
        return new Date(val).toLocaleDateString();
      }
      return String(val);
    },
  },
};
</script>

<style lang="scss" scoped>
.widget-property-filter {
  .v-list-item {
    min-height: 28px;
  }
}
</style>
